import type React from "react";
import { useCallback, useState } from "react";
import { FlatList, type LayoutChangeEvent } from "react-native";
import { Text, useDebounce, View, XStack, YStack } from "tamagui";
import { emojis } from "../../assets/emojis";
import { Input } from "./InputNew";
import { Sheet, type SheetProps } from "./Sheet";

export type Emoji = {
  emoji: string;
  name: string;
};

type EmojiCategory = keyof typeof emojis;

export const EmojiPicker: React.FC<
  { hideSearch?: boolean; onSelect?: (emoji: Emoji) => void } & SheetProps
> = ({ hideSearch, onSelect, ...sheetProps }) => {
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState<EmojiCategory>("smileys");
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined,
  );
  const debounceSearch = useDebounce(
    useCallback((text: string) => {
      setSearchText(text);
    }, []),
    500,
  );
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  let perLine: number | undefined;
  let emojiSize: number | undefined;
  let scrollHeight: number | undefined;
  let categorySize: number | undefined;

  if (containerWidth) {
    perLine = Math.floor(containerWidth / 42);
    emojiSize = containerWidth / perLine;
    scrollHeight =
      Math.min(Math.ceil(emojis.smileys.length / perLine), 5) * emojiSize +
      emojiSize / 2;
    categorySize = Math.min(
      containerWidth / Object.keys(emojis).length,
      emojiSize,
    );
  }

  const searchItems = searchText
    ? Object.values(emojis)
        .reduce<readonly Emoji[]>((acc, category) => {
          return acc.concat(category);
        }, [])
        .filter((emoji) => emoji.name.includes(searchText))
    : undefined;
  const items = searchItems ?? emojis[category];

  return (
    <Sheet
      alwaysRender={false}
      scrollable={false}
      {...sheetProps}
      sheetId="emoji"
    >
      <YStack gap="$medium">
        <XStack
          opacity={containerWidth ? 1 : 0}
          flexWrap="wrap"
          alignItems="center"
        >
          {Object.entries(emojis).map(([key, value]) => {
            const firstEmoji = value[0];
            return (
              <View
                key={firstEmoji.name}
                onPress={() => setCategory(key as EmojiCategory)}
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
                height={categorySize}
                width={categorySize}
              >
                <Text fontSize={categorySize ? categorySize - 12 : undefined}>
                  {firstEmoji.emoji}
                </Text>
              </View>
            );
          })}
        </XStack>
        {!hideSearch && (
          <Input
            id="emoji"
            onChangeText={debounceSearch}
            type="border"
            preventNewLine
            placeholder="Search emoji..."
          />
        )}
        <FlatList
          key={perLine + category}
          data={items}
          keyExtractor={(data) => data.name}
          renderItem={(data) => (
            <View
              key={data.item.name}
              onPress={() => onSelect?.(data.item)}
              cursor="pointer"
              alignItems="center"
              justifyContent="center"
              height={emojiSize}
              width={emojiSize}
            >
              <Text fontSize={emojiSize ? emojiSize - 12 : 10}>
                {data.item.emoji}
              </Text>
            </View>
          )}
          numColumns={perLine}
          onLayout={handleLayout}
          contentContainerStyle={{
            opacity: emojiSize ? 1 : 0,
            height: scrollHeight,
            maxHeight: 300,
          }}
        />
      </YStack>
    </Sheet>
  );
};
